import { useCallback, useContext } from 'react'
import MarkdownRender from 'LuxKit/MarkdownRender'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import FlashHotelAvailabilityButton from '../FlashHotelAvailabilityButton'
import OfferPageIntroInclusions from 'components/OfferPage/OfferPageIntroInclusions'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import { useIntl } from 'react-intl'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'

interface Props {
  additionalDescriptionMarkdown: string
  highlightsMarkdown?: string
  tileInclusions?: Array<App.OfferInclusion>
}

function FlashHotelInclusionsModal(props: Props) {
  const {
    additionalDescriptionMarkdown,
    highlightsMarkdown,
    tileInclusions,
  } = props

  const intl = useIntl()
  const { offer } = useContext(OfferPageStateContext)
  const showExperiencesPassport = isFlashHotelOffer(offer)
  const { resolve } = useModalElementContext()
  const onCTAClick = useCallback(() => resolve(), [resolve])
  const hasTileInclusions = !!tileInclusions?.length
  const hasAdditionalDescription = !!additionalDescriptionMarkdown
  const hasHighlights = !!highlightsMarkdown

  const modalTitle = hasHighlights
    ? intl.formatMessage({
        id: 'hhIh2-sP0z1PNq4YWSId',
        defaultMessage: 'Inclusions & Highlights',
        description: 'Title for inclusions and highlights modal dialog window',
      })
    : intl.formatMessage({
        id: 'lfAM_dAM-iBhofbA-nj8',
        defaultMessage: 'Inclusions',
        description: 'Title for the flash hotel inclusions modal dialog window',
      })

  return (
    <ModalBase>
      <ModalHeader title={modalTitle} />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            {hasTileInclusions && (
              <Group direction="vertical" gap={8}>
                <HeadingTextBlock variant="heading4">
                  {intl.formatMessage({
                    id: 'YvEW3hh5xi4-UyzKYf4e',
                    defaultMessage: 'Your handpicked inclusions',
                    description: 'Heading for handpicked inclusions in the offer inclusions section',
                  })}
                </HeadingTextBlock>
                <Group direction="vertical" gap={4}>
                  <OfferPageIntroInclusions tileInclusionsList={tileInclusions} />
                  <LoyaltyInclusions offer={offer} variant="offer-page" size="large" />
                </Group>
              </Group>
            )}
            {hasTileInclusions && (hasAdditionalDescription || hasHighlights) && <Divider kind="secondary" />}
            {hasAdditionalDescription && (
              <MarkdownRender type="compact" content={additionalDescriptionMarkdown} />
            )}
            {hasHighlights && <>
              {hasAdditionalDescription && <Divider kind="secondary" />}
              <HeadingTextBlock variant="heading4">
                {intl.formatMessage({
                  id: 'YpVyllxGgOk_XH6X4jwN',
                  defaultMessage: 'Highlights',
                  description: 'Section heading for the highlights section on the offer detail page',
                })}
              </HeadingTextBlock>
              <MarkdownRender type="compact" fontSize="large" content={highlightsMarkdown} />
            </>}
            {showExperiencesPassport && <ExperiencesPassportOfferSummary />}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <SoftOfferPageWalledGarden>
        <ModalFooter>
          <FlashHotelAvailabilityButton onClick={onCTAClick} />
        </ModalFooter>
      </SoftOfferPageWalledGarden>
    </ModalBase>
  )
}

export default FlashHotelInclusionsModal
